import React, { FC, PropsWithChildren, ReactNode, useState } from 'react'

import styled, { useTheme } from 'styled-components'

import ExclamationIcon from 'src/Assets/Svg/pages-icons/Exclamation.svg?react'
import ErrorIcon from 'src/Assets/Svg/pages-icons/Error.svg?react'
import SuccessIcon from 'src/Assets/Svg/pages-icons/Success.svg?react'
import CloseIcon from 'src/Assets/Svg/pages-icons/Close.svg?react'
import InfoIcon from 'src/Assets/Svg/pages-icons/Info.svg?react'

type AlertType = 'success' | 'danger' | 'warning' | 'grey'

type RenderButtonProps = {
  onClose: () => void
}

export type PrimaryAlertProps = PropsWithChildren & {
  type: AlertType
  hasCross?: boolean
  className?: string
  title?: string
  renderButtons?: (props: RenderButtonProps) => ReactNode
  children?: ReactNode
  $width?: string
  onClose?: () => void
}

const Container = styled.div<{
  $borderColor: string
  $width: string
  $hasCross: boolean
}>`
  display: flex;
  position: relative;
  padding: 24px ${({ $hasCross }) => ($hasCross ? '64px' : '24px')} 24px 24px;
  align-items: flex-start;
  gap: 16px;
  border-radius: 16px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  background: ${({ theme }) => theme.color.white};
  width: ${({ $width }) => $width};
`

const IconBlock = styled.div<{ $iconColor: string }>`
  display: flex;
  padding-top: 4px;
  align-items: flex-start;
  gap: 10px;

  svg {
    path {
      fill: ${({ $iconColor }) => $iconColor};
    }
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`
const Message = styled(Title)`
  font-weight: 400;
  padding-bottom: 4px;
`

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  display: flex;
  height: 40px;
  width: 40px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 40px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.color.lightGrey};
  }
`

const StyledCloseIcon = styled(CloseIcon)`
  fill: ${({ theme }) => theme.color.darkGrey};
  width: 16px;
  height: 16px;
`

const StyledSuccessIcon = styled(SuccessIcon)`
  width: 16px;
  height: 16px;
`

const MAP_TYPE_ICON = {
  success: <StyledSuccessIcon />,
  warning: <ExclamationIcon />,
  danger: <ErrorIcon />,
  grey: <InfoIcon />,
}

export const PrimaryAlert: FC<PrimaryAlertProps> = ({
  type,
  $width,
  title,
  children,
  hasCross = true,
  className,
  renderButtons,
  onClose,
}) => {
  const theme = useTheme()
  const [visible, setVisible] = useState(true)
  if (!visible) {
    return null
  }

  const MAP_TYPE_TO_BORDER_COLOR = {
    success: theme.color.brandGreen,
    danger: theme.color.mediumRed,
    warning: theme.color.mediumOrange,
    grey: theme.color.slateGrey,
  }

  const MAP_TYPE_TO_ICON_COLOR = {
    success: theme.color.brandGreen,
    danger: theme.color.mediumRed,
    warning: theme.color.mediumOrange,
    grey: theme.color.darkGrey,
  }

  const handleClose = (): void => {
    setVisible(false)
    onClose?.()
  }
  return (
    <Container
      $hasCross={hasCross}
      $width={$width || '100%'}
      $borderColor={MAP_TYPE_TO_BORDER_COLOR[type]}
      className={className}
    >
      <IconBlock $iconColor={MAP_TYPE_TO_ICON_COLOR[type]}>
        {MAP_TYPE_ICON[type]}
      </IconBlock>
      <Content>
        {title && <Title>{title}</Title>}
        <Message>{children}</Message>
        {renderButtons?.({ onClose: handleClose })}
      </Content>
      {hasCross && (
        <CloseButton onClick={handleClose}>
          <StyledCloseIcon />
        </CloseButton>
      )}
    </Container>
  )
}
